import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Plus, Search } from "lucide-react";
import { Button, Input, buttonVariants } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Select } from "@/components/ui/Select";
import { cn } from "@/design-system/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { TaskStatusPill } from "./components/TaskStatusPill";
import { TaskPriorityBadge } from "./components/TaskPriorityBadge";
import { TaskDueDatePill } from "./components/TaskDueDatePill";
import { TaskAssigneeAvatar } from "./components/TaskAssigneeAvatar";
import { TaskTagChips } from "./components/TaskTagChips";
import { QuickAddTaskModal } from "./components/QuickAddTaskModal";
import {
  useWorkTasks,
  useTaskTags,
} from "./hooks/useWorkTasks";
import { useQuickAddShortcut } from "./hooks/useQuickAddShortcut";
import { buildTaskExportUrl } from "@/api/workTasks";
import { extractErrorMessage } from "@/api/client";
import type {
  WorkTaskDto,
  WorkTaskListQuery,
  WorkTaskPriority,
  WorkTaskStatus,
} from "@/api/workTasks";

type Tab = "mine" | "team" | "createdByMe" | "all";

const TABS: { key: Tab; label: string; viewAllOnly?: boolean }[] = [
  { key: "mine", label: "My open" },
  { key: "team", label: "Team" },
  { key: "createdByMe", label: "Assigned by me" },
  { key: "all", label: "All", viewAllOnly: true },
];

export function TasksListPage() {
  const { user, hasPermission } = useAuth();
  const canCreate = hasPermission("Tasks.Create");
  const canViewAll = hasPermission("Tasks.ViewAll");

  const [tab, setTab] = useState<Tab>("mine");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<WorkTaskStatus | "">("");
  const [priorityFilter, setPriorityFilter] = useState<WorkTaskPriority | "">("");
  const [tagFilter, setTagFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [quickOpen, setQuickOpen] = useState(false);

  useQuickAddShortcut(() => setQuickOpen(true), canCreate);

  const tagsQ = useTaskTags();

  const query: WorkTaskListQuery = useMemo(() => {
    const base: WorkTaskListQuery = {
      page,
      pageSize: 25,
      sortBy: "dueDate",
      sortDirection: "asc",
    };
    if (search.trim()) base.q = search.trim();
    if (statusFilter) base.status = statusFilter;
    if (priorityFilter) base.priority = priorityFilter;
    if (tagFilter) base.tagId = tagFilter;
    if (tab === "mine") base.scope = "me";
    // Team tab — my multiuser tasks (owner OR participant), all statuses,
    // honoring the same filters as the other tabs.
    if (tab === "team") base.scope = "team";
    if (tab === "createdByMe" && user) base.createdById = user.id;
    // "all" tab: no extra creator/assignee filter — server applies
    // visibility based on Tasks.ViewAll automatically.
    return base;
  }, [page, search, statusFilter, priorityFilter, tagFilter, tab, user]);

  const list = useWorkTasks(query);

  const columns: Column<WorkTaskDto>[] = [
    {
      key: "title",
      header: "Title",
      render: (t) => (
        <Link
          to={`/tasks/${t.id}`}
          className="font-semibold text-onyx hover:text-gold-shadow line-clamp-2"
        >
          {t.title}
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (t) => <TaskStatusPill status={t.status} percent={t.progressPercent} />,
    },
    {
      key: "priority",
      header: "Priority",
      render: (t) => <TaskPriorityBadge priority={t.priority} />,
    },
    {
      key: "assignee",
      header: "Assignee",
      render: (t) => <TaskAssigneeAvatar assignee={t.assignee} />,
    },
    {
      key: "due",
      header: "Due",
      render: (t) =>
        t.status === "Done" ? (
          <span className="text-label text-gray-mid">—</span>
        ) : (
          <TaskDueDatePill dueDate={t.dueDate} />
        ),
    },
    {
      key: "tags",
      header: "Tags",
      render: (t) => <TaskTagChips tags={t.tags} />,
    },
  ];

  const visibleTabs = TABS.filter((t) => !t.viewAllOnly || canViewAll);

  return (
    <>
      <PageHeader
        eyebrow="Home"
        title="Tasks & Todos"
        actions={
          <div className="flex items-center gap-space-2">
            {canViewAll && (
              <a
                href={buildTaskExportUrl(query)}
                download
                className={cn(buttonVariants({ variant: "icon", size: "sm" }), "w-8 px-0")}
                aria-label="Export tasks as CSV"
                title="Export CSV"
              >
                <Download size={14} aria-hidden />
              </a>
            )}
            {canCreate ? (
              <Button size="sm" onClick={() => setQuickOpen(true)} type="button">
                <Plus size={14} aria-hidden /> New task
              </Button>
            ) : (
              <Button size="sm" disabled title="You do not have Tasks.Create permission." type="button">
                <Plus size={14} aria-hidden /> New task
              </Button>
            )}
          </div>
        }
      />

      {/* Tab strip */}
      <div className="flex gap-space-3 border-b border-border-light mb-space-4">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => {
              setTab(t.key);
              setPage(1);
            }}
            className={
              "py-space-2 -mb-px font-display font-semibold text-label uppercase tracking-label " +
              (tab === t.key
                ? "text-onyx border-b-2 border-gold"
                : "text-gray-mid hover:text-onyx")
            }
            aria-pressed={tab === t.key}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filter row */}
      <div className="flex flex-col md:flex-row md:items-end gap-space-3 mb-space-4">
        <div className="flex-1 min-w-0">
          <div className="relative">
            <Search
              size={16}
              aria-hidden
              className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
            />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search title or description"
              className="pl-space-5"
              aria-label="Search tasks"
            />
          </div>
        </div>
        <div className="flex gap-space-2">
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as WorkTaskStatus | "");
              setPage(1);
            }}
            aria-label="Status filter"
          >
            <option value="">Any status</option>
            <option value="Open">Open</option>
            <option value="InProgress">In progress</option>
            <option value="Done">Done</option>
          </Select>
          <Select
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value as WorkTaskPriority | "");
              setPage(1);
            }}
            aria-label="Priority filter"
          >
            <option value="">Any priority</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </Select>
          <Select
            value={tagFilter}
            onChange={(e) => {
              setTagFilter(e.target.value);
              setPage(1);
            }}
            aria-label="Tag filter"
          >
            <option value="">Any tag</option>
            {(tagsQ.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.displayName}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={list.data?.data ?? []}
        loading={list.isLoading}
        error={list.isError ? extractErrorMessage(list.error) : null}
        emptyMessage="No tasks yet — create one to get started."
        rowKey={(t) => t.id}
      />

      {list.data && list.data.totalCount > list.data.pageSize && (
        <div className="flex items-center justify-between mt-space-3">
          <span className="text-label text-gray-mid">
            Page {list.data.page} of {Math.ceil(list.data.totalCount / list.data.pageSize)} ·{" "}
            {list.data.totalCount} total
          </span>
          <div className="flex gap-space-2">
            <Button
              variant="secondary"
              size="sm"
              type="button"
              disabled={list.data.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              disabled={
                list.data.page >= Math.ceil(list.data.totalCount / list.data.pageSize)
              }
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <QuickAddTaskModal
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        navigateOnSave
      />
    </>
  );
}
